import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { PerformanceSummary } from '../../../features/performance_reviews/types.js'
import type { WorkerTableRow } from '../../roster/index.js'
import { ADMIN_API_BASE_URL, EndpointHint, Skeleton } from '../../core/index.js'
import { generatePerformanceSummary, listPerformanceSummaries, type PartnerSession } from '../lib/partnerApi.js'
import { FileText } from 'lucide-react'
import { ErrorNote, INPUT_CLASS, Labeled, describeError } from './formControls.js'
import { Button } from '../../core/index.js'

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/

/**
 * Plays the client's HR integration: generates a performance summary for a
 * worker with `POST /workers/{workerId}/performance-summaries` and lists
 * what has been generated, so an integration can be rehearsed before a
 * connector is written. Needs the `performance:read` scope on the token and
 * the Performance Summaries add-on enabled at the worker's facility.
 */
export function PerformanceSummaryForm({ session, workers }: { session: PartnerSession; workers: WorkerTableRow[] }) {
  const queryClient = useQueryClient()
  const [workerId, setWorkerId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [note, setNote] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<PerformanceSummary | null>(null)

  const listKey = ['partner-simulator', 'performance-summaries', session.clientId, workerId]
  const listQuery = useQuery({
    queryKey: listKey,
    queryFn: () => listPerformanceSummaries(ADMIN_API_BASE_URL ?? '', session, workerId),
    enabled: workerId !== '' && Boolean(ADMIN_API_BASE_URL),
    staleTime: 0,
  })

  const spanGiven = from.trim() !== '' || to.trim() !== ''
  const spanValid = !spanGiven || (MONTH_PATTERN.test(from.trim()) && MONTH_PATTERN.test(to.trim()) && from.trim() <= to.trim())
  const canSend = workerId !== '' && spanValid && !isSending

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!canSend || !ADMIN_API_BASE_URL) {
      return
    }
    setIsSending(true)
    setError(null)
    setSummary(null)
    try {
      const generated = await generatePerformanceSummary(ADMIN_API_BASE_URL, session, workerId, {
        ...(spanGiven ? { span: { from: from.trim(), to: to.trim() } } : {}),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      })
      setSummary(generated)
      void queryClient.invalidateQueries({ queryKey: listKey })
    } catch (err) {
      setError(describeError(err, 'Generating the summary failed.'))
    } finally {
      setIsSending(false)
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
      <div className="grid gap-5 sm:grid-cols-2">
        <Labeled label="Worker" htmlFor="performance-worker" hint={workers.length === 0 ? 'This facility has no workers on its roster yet.' : undefined}>
          <select id="performance-worker" className={INPUT_CLASS} value={workerId} onChange={(event) => setWorkerId(event.target.value)}>
            <option value="">Choose a worker…</option>
            {workers.map((worker) => (
              <option key={worker.workerId} value={worker.workerId}>
                {worker.name} ({worker.workerId})
              </option>
            ))}
          </select>
        </Labeled>
        <Labeled label="Span (optional)" htmlFor="performance-from" hint="Whole months, YYYY-MM to YYYY-MM, at most 24. Left blank: the calendar year to date, closed months only.">
          <div className="flex items-center gap-2">
            <input id="performance-from" className={`${INPUT_CLASS} font-mono`} placeholder="2026-01" value={from} onChange={(event) => setFrom(event.target.value)} />
            <span className="text-xs text-ink-mid">to</span>
            <input id="performance-to" aria-label="Span end" className={`${INPUT_CLASS} font-mono`} placeholder="2026-09" value={to} onChange={(event) => setTo(event.target.value)} />
          </div>
        </Labeled>
      </div>
      <Labeled label="Context note (optional)" htmlFor="performance-note" hint="One line the HR system will see beside the summary, kept as written.">
        <input id="performance-note" className={INPUT_CLASS} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
      </Labeled>

      <Button icon={FileText} size="lg" type="submit" disabled={!canSend}>
        {isSending ? 'Generating…' : 'Generate summary'}
      </Button>
      <EndpointHint className="ml-3">POST /workers/{workerId || '{workerId}'}/performance-summaries</EndpointHint>
      {!spanValid && <p className="text-[11px] font-bold text-coral">Both months are needed, as YYYY-MM, the first no later than the second.</p>}
      {error && <ErrorNote message={error} />}

      {summary && (
        <div className="border border-marine/15 bg-canvas p-4 space-y-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">
            Summary <span className="font-mono normal-case tracking-normal">{summary.summaryId}</span>
          </div>
          <p className="text-xs text-ink">
            <span className="font-bold">{summary.worker.name}</span>, {summary.span.from} to {summary.span.to}: {summary.provenance.closedMonths} closed {summary.provenance.closedMonths === 1 ? 'month' : 'months'},{' '}
            {summary.shifts.scheduled} scheduled shifts, {summary.shifts.callOuts} call-outs, tier {summary.tierAtStart ?? '—'} to {summary.tierAtEnd ?? '—'}.
          </p>
          <p className="text-[11px] italic text-ink-mid">{summary.provenance.statement}</p>
          <div className="text-[10px] font-mono text-ink-mid break-all">fingerprint {summary.contentHash}</div>
          <details>
            <summary className="text-[11px] font-bold text-marine cursor-pointer">The whole record</summary>
            <pre className="mt-2 bg-white border border-marine/15 p-3 text-[11px] font-mono text-ink whitespace-pre-wrap break-all max-h-96 overflow-auto">{JSON.stringify(summary, null, 2)}</pre>
          </details>
        </div>
      )}

      {workerId !== '' && (
        <div>
          <EndpointHint className="mb-1 block">GET /workers/{workerId}/performance-summaries</EndpointHint>
          {listQuery.isPending ? (
            <Skeleton label="Loading summaries" />
          ) : listQuery.isError ? (
            <ErrorNote message={describeError(listQuery.error, 'Could not list this worker’s summaries.')} onRetry={() => void listQuery.refetch()} retrying={listQuery.isFetching} />
          ) : listQuery.data.length === 0 ? (
            <p className="text-xs text-ink-mid">No summary has been generated for this worker yet.</p>
          ) : (
            <ul className="border border-marine/15 divide-y divide-marine/10 text-xs">
              {listQuery.data.map((item) => (
                <li key={item.summaryId} className="px-3 py-1.5 flex flex-wrap gap-x-3 gap-y-1">
                  <span className="font-mono">{item.summaryId}</span>
                  <span>
                    {item.span.from} to {item.span.to}
                  </span>
                  <span className="text-ink-mid">
                    {new Date(item.generatedAt).toLocaleString()} by {item.generatedBy.name ?? item.generatedBy.kind}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </form>
  )
}
